import { useContext, useCallback, useState } from 'react';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _orderBy from 'lodash/orderBy';
import { formatListData, AjaxContext, AppContext, EMPTY_ARRAY, UserContext, EAPKArchs, GAME_NAMES, Apk } from 'commons-ui';
import { ZOOKEEPER_ENDPOINTS, GAME_KEY_MAP } from '../../admin/zooKeeper/utils/helper';
import { APK_UPDATE_SEGMENT_ZK_VALUES, APK_UPDATE_ZK_VALUES, EWebsiteUpdateDashBoardURL } from './helper';
import { IWebsiteUpdateV2 } from './apkUpdateInfoModel';

const useService = () => {
	const { postData, getData } = useContext(AjaxContext);
	const { user } = useContext(UserContext);
	const [gameNameVersionMap, setGameNameVersionMap] = useState<{ [key: string]: number }>();
	const [apksVersions, setAPKVersions] = useState<any>();

	const {
		app: { appName }
	} = useContext(AppContext);
	const getIndexOfSegmentAppUpdate = (appUpdatesList, version) => {
		let index = -1;
		appUpdatesList.forEach((appUpdate, i) => {
			if (appUpdate.sourceAppVersionRange === version) {
				index = i;
			}
		});
		return index;
	};

	const requestGetApkVersionInfoList = useCallback(
		async ({ pageDetails, sortDetails, filterDetails }: { pageDetails: any; sortDetails: any; filterDetails: any }) => {
			const result = await postData({
				endpoint: '/marketing/dashboard/apkUpdateInfo/getApkVersions',
				queryParams: {
					appName
				},
				body: {
					page: pageDetails,
					filters: filterDetails,
					sortInfoArray: sortDetails
				}
			});
			return {
				rowCount: _get(result, ['data', 'totalCount'], 0),
				data: formatListData(_get(result, ['data', 'apks'], []))
			};
		},
		[]
	);

	const requestGetApkVersionInfo = useCallback(
		async ({ versionNumber, gameName, arch }: { versionNumber: any; gameName: string; arch?: string }) => {
			const result = await postData({
				endpoint: '/marketing/dashboard/apkUpdateInfo/getApkVersion',
				queryParams: {
					appName
				},
				body: {
					versionNumber,
					gameName,
					arch
				}
			});
			return _get(result, ['data', 'apk'], []);
		},
		[]
	);

	const requestGetWebsiteUpdateInfoList = useCallback(
		async ({ pageDetails, sortDetails, filterDetails }: { pageDetails: any; sortDetails: any; filterDetails: any }) => {
			const result = await postData({
				endpoint: '/marketing/dashboard/apkUpdateInfo/getWebsiteUpdates',
				queryParams: {
					appName
				},
				body: {
					page: pageDetails,
					filters: filterDetails,
					sortInfoArray: sortDetails
				}
			});
			return {
				rowCount: _get(result, ['data', 'totalCount'], 0),
				data: formatListData(_get(result, ['data', 'websiteUpdates'], []))
			};
		},
		[]
	);

	const requestGetDashBoardNames = useCallback(async () => {
		const result = await postData({
			endpoint: EWebsiteUpdateDashBoardURL.GET_DASHBOARD_NAME,
			queryParams: {
				appName
			}
		});
		return result;
	}, []);

	const requestGetTestBucketsNames = useCallback(async () => {
		const result = await postData({
			endpoint: EWebsiteUpdateDashBoardURL.GET_TESTBUCKETS_NAME,
			queryParams: {
				appName
			}
		});
		return result;
	}, []);

	const requestGetWebsiteUpdateInfoListV2 = useCallback(
		async ({ pageDetails, sortDetails, filterDetails }: { pageDetails: any; sortDetails: any; filterDetails: any }) => {
			const result = await postData({
				endpoint: EWebsiteUpdateDashBoardURL.GET_WEBSITE_UPDATES_V2,
				queryParams: {
					appName
				},
				body: {
					page: pageDetails,
					filters: filterDetails,
					sortInfoArray: sortDetails
				}
			});
			return {
				rowCount: _get(result, ['data', 'totalCount'], 0),
				data: formatListData(_get(result, ['data', 'websiteUpdates'], []))
			};
		},
		[]
	);

	const requestCreateApkVersionInfo = useCallback((apkVersionInfo: any) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/addApkVersion',
			body: apkVersionInfo,
			queryParams: {
				appName
			}
		});
	}, []);

	const requestCreateWebsiteUpdateInfo = useCallback((websiteUpdateInfo: any) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/addWebsiteUpdateInfo',
			body: websiteUpdateInfo,
			queryParams: {
				appName
			}
		});
	}, []);

	const requestCreateWebsiteUpdateInfoV2 = useCallback((websiteUpdateInfo: IWebsiteUpdateV2) => {
		return postData({
			endpoint: EWebsiteUpdateDashBoardURL.ADD_WEBSITE_UPDATE_INFO_V2,
			body: websiteUpdateInfo,
			queryParams: {
				appName
			}
		});
	}, []);
	const requestUpdateApkVersionInfo = useCallback((apkVersionInfo: any) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/updateApkVersion',
			body: apkVersionInfo,
			queryParams: {
				appName
			}
		});
	}, []);

	const requestUpdateWebsiteUpdateInfo = useCallback((websiteUpdateInfo: any) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/updateWebsiteUpdateInfo',
			body: websiteUpdateInfo,
			queryParams: {
				appName
			}
		});
	}, []);

	const requestUpdateWebsiteUpdateInfoV2 = useCallback((websiteUpdateInfo: IWebsiteUpdateV2) => {
		return postData({
			endpoint: EWebsiteUpdateDashBoardURL.UPDATE_WEBSITE_UPDATE_INFO_V2,
			body: websiteUpdateInfo,
			queryParams: {
				appName
			}
		});
	}, []);
	const requestDeployWebsiteUpdateInfo = useCallback((websiteUpdateInfoId: string) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/deployWebsiteUpdate',
			body: { _id: websiteUpdateInfoId },
			queryParams: {
				appName
			}
		});
	}, []);

	const requestDeployWebsiteUpdateInfoV2 = useCallback((websiteUpdateInfoId: string) => {
		return postData({
			endpoint: EWebsiteUpdateDashBoardURL.DEPLOY_WEBSITE_UPDATES_V2,
			body: { _id: websiteUpdateInfoId },
			queryParams: {
				appName
			}
		});
	}, []);

	function removeDuplicatesByVersionGameNameCombination(arr: Apk[]) {
		const uniqueCombinations = new Set();

		return arr.filter((obj) => {
			const combination = obj.versionNumber.toString() + obj.gameName;
			if (uniqueCombinations.has(combination)) {
				return false; // Duplicate combination found, exclude from result
			}
			uniqueCombinations.add(combination);
			return true;
		});
	}

	const getLudoVersionNumberOptions = useCallback(async () => {
		const apkVersions = await requestGetApkVersionInfoList({
			pageDetails: { pageNo: 1, pageSize: Number.MAX_SAFE_INTEGER },
			sortDetails: EMPTY_ARRAY,
			filterDetails: EMPTY_ARRAY
		});
		const result = _map(
			_orderBy(
				_get(apkVersions, 'data', []).filter((apk) => apk.gameName === GAME_NAMES.LUDO.toLowerCase()),
				['versionNumber'],
				['desc']
			),
			(apk) => ({
				id: apk.versionNumber.toString(),
				label: `${apk.versionNumber} - ${apk.versionName}`,
				gameName: `${apk.gameName}`
			})
		);
		return result;
	}, []);

	const getVersionNumberOptions = useCallback(async () => {
		const apkVersions = await requestGetApkVersionInfoList({
			pageDetails: { pageNo: 1, pageSize: Number.MAX_SAFE_INTEGER },
			sortDetails: EMPTY_ARRAY,
			filterDetails: EMPTY_ARRAY
		});

		const gameVersionMap: { [key: string]: number } = {};

		_get(apkVersions, 'data', []).forEach((apk: Apk) => {
			gameVersionMap[`${apk.versionNumber} - ${apk.versionName} - ${apk.gameName}`] = apk.versionNumber;
		});
		setGameNameVersionMap(gameVersionMap);
		const result = _map(
			_orderBy(removeDuplicatesByVersionGameNameCombination(_get(apkVersions, 'data', [])), ['versionNumber'], ['desc']),
			(apk) => ({
				id: `${apk.versionNumber} - ${apk.versionName} - ${apk.gameName}`,
				label: `${apk.versionNumber} - ${apk.versionName}`,
				gameName: `${apk.gameName}`
			})
		);
		setAPKVersions(result);
		return result;
	}, []);

	const getVersionNumberFromKey = (key: string): number | null => {
		return gameNameVersionMap ? gameNameVersionMap[key] : null;
	};

	const filterVersionCodesByGameName = (gameName: string) => {
		if (!apksVersions) {
			getVersionNumberOptions();
		}
		return apksVersions?.filter((entry: any) => gameName?.includes(entry.gameName.toLowerCase()));
	};

	const getKeyValue = async ({ keyName, serviceName }: { keyName: string; serviceName: string }) => {
		const res = await getData({
			endpoint: ZOOKEEPER_ENDPOINTS.GET_KEY,
			queryParams: {
				serviceName,
				game: GAME_KEY_MAP[appName],
				key: keyName,
				appName
			}
		});
		return res.data;
	};

	const getSegmentAppUpdateData = (element) => {
		const appUpdateInfo = element;
		const versionRange = appUpdateInfo.sourceAppVersionRange.split('-');
		appUpdateInfo.versionNumber = appUpdateInfo.targetVersionCode;
		const formattedData = {
			targetVersionCode: appUpdateInfo.targetVersionCode,
			descriptions: appUpdateInfo.descriptions,
			minVersion: versionRange[0],
			maxVersion: versionRange[1],
			deviceType: appUpdateInfo.packageName === 'in.ludo.supremegold' ? 'android' : 'ios',
			isABTestOn: appUpdateInfo.isABTestOn,
			versionName: appUpdateInfo.versionName,
			isForceUpdate: appUpdateInfo.isForceUpdate,
			forceBeforeLogin: appUpdateInfo.forceBeforeLogin,
			ludoAppApkUrl: appUpdateInfo.ludoAppApkUrl,
			ludoSupremeAppUpdateUrl: appUpdateInfo.ludoSupremeAppUpdateUrl,
			updateType: appUpdateInfo.updateType,
			segments: appUpdateInfo.segments.join(','),
			isSegmentWiseUpdateReq: true
		};
		return formattedData;
	};

	const requestGetAppUpdatesList = async ({ keyName, serviceName }: { keyName: string; serviceName: string }) => {
		const result = await getKeyValue({ keyName, serviceName });
		const appUpdatesList: any[] = [];
		if (result.success) {
			Object.keys(result.value).some((key) => {
				const versionRange = key.split('-');
				const appUpdateInfo = result.value[key];
				appUpdateInfo.versionNumber = appUpdateInfo.targetVersionCode;
				const formattedData = {
					targetVersionCode: appUpdateInfo.targetVersionCode,
					descriptions: appUpdateInfo.descriptions,
					minVersion: versionRange[0],
					maxVersion: versionRange[1],
					deviceType: appUpdateInfo.packageName === 'in.ludo.supremegold' ? 'android' : 'ios',
					isABTestOn: appUpdateInfo.isABTestOn,
					versionName: appUpdateInfo.versionName,
					isForceUpdate: appUpdateInfo.isForceUpdate,
					forceBeforeLogin: appUpdateInfo.forceBeforeLogin,
					ludoAppApkUrl: appUpdateInfo.ludoAppApkUrl,
					ludoSupremeAppUpdateUrl: appUpdateInfo.ludoSupremeAppUpdateUrl
				};
				appUpdatesList.push(formattedData);
				return false;
			});
		}
		return { list: appUpdatesList, totalCount: appUpdatesList.length };
	};

	const requestGetAppUpdatesListOfSegment = async ({ keyName, serviceName }: { keyName: string; serviceName: string }) => {
		const result = await getKeyValue({ keyName, serviceName });
		const appUpdatesList: any[] = [];
		if (result.success && result.value) {
			result.value.forEach((element: any) => {
				const formattedData = getSegmentAppUpdateData(element);
				appUpdatesList.push(formattedData);
				return false;
			});
		}
		return { list: appUpdatesList, totalCount: appUpdatesList.length };
	};

	const requestGetAppUpdateInfoList = useCallback(async () => {
		const result = await requestGetAppUpdatesList({
			keyName: APK_UPDATE_ZK_VALUES.keyName,
			serviceName: APK_UPDATE_ZK_VALUES.serviceName
		});
		const resultOfSegmentUpdate = await requestGetAppUpdatesListOfSegment({
			keyName: APK_UPDATE_SEGMENT_ZK_VALUES.keyName,
			serviceName: APK_UPDATE_SEGMENT_ZK_VALUES.serviceName
		});
		result.list.push(...resultOfSegmentUpdate.list);
		result.totalCount += resultOfSegmentUpdate.totalCount;
		return {
			rowCount: _get(result, ['totalCount'], 0),
			data: formatListData(_get(result, ['list'], []))
		};
	}, []);

	const getZKLogs = async ({ keyName, serviceName }: { keyName: string; serviceName: string }) => {
		const res = await getData({
			endpoint: ZOOKEEPER_ENDPOINTS.GET_LOGS,
			queryParams: {
				serviceName,
				game: 'ludo',
				key: keyName,
				appName
			}
		});
		return res.data;
	};

	const requestGetSuperAppUpdateLogs = useCallback(async () => {
		const result = await getZKLogs({
			keyName: APK_UPDATE_ZK_VALUES.keyName,
			serviceName: APK_UPDATE_ZK_VALUES.serviceName
		});
		return {
			rowCount: _get(result, ['totalCount'], 0),
			data: formatListData(_get(result, ['logs'], []))
		};
	}, []);

	const requestGetSubAppUpdateLogsList = async ({ keys, serviceName }: { keys: string[]; serviceName: string }) => {
		const promises: Array<Promise<any>> = [];
		let subappUpdateLogsList: any[] = [];
		keys.forEach(async (keyName) => {
			promises.push(getZKLogs({ keyName, serviceName }));
		});
		const allResult = await Promise.all(promises);
		allResult.forEach((result) => {
			if (result.success) {
				subappUpdateLogsList = [...subappUpdateLogsList, ...result.logs];
			}
		});
		return { logs: subappUpdateLogsList, totalCount: subappUpdateLogsList.length };
	};

	const requestGetSubAppUpdateLogsInfo = useCallback(async () => {
		const result = await requestGetSubAppUpdateLogsList({
			keys: APK_UPDATE_ZK_VALUES.subAppkeyNames,
			serviceName: APK_UPDATE_ZK_VALUES.serviceName
		});
		return {
			rowCount: _get(result, ['totalCount'], 0),
			data: formatListData(_get(result, ['logs'], []))
		};
	}, []);

	const requestGetSubAppUpdatesList = async ({ keys, serviceName }: { keys: string[]; serviceName: string }) => {
		const promises: Array<Promise<any>> = [];
		const subappUpdatesList: any[] = [];
		keys.forEach(async (keyName) => {
			promises.push(getKeyValue({ keyName, serviceName }));
		});
		const allResult = await Promise.all(promises);
		allResult.forEach((result) => {
			if (result.success) {
				const subappUpdateInfo = {
					gameName: result.path.split('/')[result.path.split('/').length - 1],
					isForceUpdate: result.value.isForceUpdate,
					apkUrlArmV7: result.value.apkUrlArmV7,
					apkUrlArmV8: result.value.apkUrlArmV8,
					versionCode: result.value.versionCode
				};
				subappUpdatesList.push(subappUpdateInfo);
			}
		});
		return { list: subappUpdatesList, totalCount: subappUpdatesList.length };
	};

	const requestGetSubAppUpdateInfoList = useCallback(async () => {
		const result = await requestGetSubAppUpdatesList({
			keys: APK_UPDATE_ZK_VALUES.subAppkeyNames,
			serviceName: APK_UPDATE_ZK_VALUES.serviceName
		});
		return {
			rowCount: _get(result, ['totalCount'], 0),
			data: formatListData(_get(result, ['list'], []))
		};
	}, []);

	const requestCreateAppUpdateInfo = useCallback(async (appUpdateInfo: any) => {
		let { serviceName, keyName } = APK_UPDATE_ZK_VALUES;
		let result = null;
		if (appUpdateInfo.isSegmentWiseUpdate) {
			serviceName = APK_UPDATE_SEGMENT_ZK_VALUES.serviceName;
			keyName = APK_UPDATE_SEGMENT_ZK_VALUES.keyName;
		}
		result = await getKeyValue({ keyName, serviceName });
		let apkVersion: any;
		if (appUpdateInfo.deviceType !== 'ios') {
			apkVersion = await requestGetApkVersionInfo({
				versionNumber: appUpdateInfo.targetVersionCode,
				gameName: 'ludo'
			});
		}
		const data = {
			targetVersionCode: appUpdateInfo.deviceType === 'ios' ? 0 : appUpdateInfo.targetVersionCode,
			versionName: appUpdateInfo.versionName,
			isAvailableOnPlayStore: appUpdateInfo.isAvailableOnPlayStore,
			isForceUpdate: appUpdateInfo.isForceUpdate,
			forceBeforeLogin: appUpdateInfo.forceBeforeLogin,
			descriptions: appUpdateInfo.descriptions,
			ludoAppApkUrl: appUpdateInfo.deviceType === 'ios' ? '' : apkVersion.apkLinkUrl,
			ludoSupremeAppUpdateUrl: appUpdateInfo.ludoSupremeAppUpdateUrl,
			packageName: appUpdateInfo.deviceType === 'ios' ? 'in.ios.ludosupremegold' : 'in.ludo.supremegold',
			isABTestOn: appUpdateInfo.isABTestOn,
			sourceAppVersionRange: appUpdateInfo.isSegmentWiseUpdate
				? `${appUpdateInfo.minVersion}-${appUpdateInfo.maxVersion}`
				: undefined,
			updateType: appUpdateInfo.isSegmentWiseUpdate ? appUpdateInfo.updateType : undefined,
			segments: appUpdateInfo.isSegmentWiseUpdate ? appUpdateInfo?.segments.split(',') : undefined
		};
		if (result.success && appUpdateInfo.isSegmentWiseUpdate) {
			const index = getIndexOfSegmentAppUpdate(result.value, data.sourceAppVersionRange);
			if (index > -1) {
				result.value[index] = data;
			} else {
				result.value.push(data);
			}
		} else {
			result.value[`${appUpdateInfo.minVersion}-${appUpdateInfo.maxVersion}`] = data;
		}
		return postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_KEY,
			queryParams: {
				serviceName,
				game: 'ludo',
				key: keyName,
				appName
			},
			body: {
				data: result.value,
				agent: _get(user, 'userName', ''),
				desc: 'adding app updates for ludo'
			}
		});
	}, []);

	const requestUpdateAppUpdateInfo = useCallback((appUpdateInfo: any) => {
		return requestCreateAppUpdateInfo(appUpdateInfo);
	}, []);

	const requestCreateSubAppUpdateInfo = useCallback(async (subAppUpdateInfo: any) => {
		const initialZkValue = await getKeyValue({
			keyName: subAppUpdateInfo.gameName.toUpperCase(),
			serviceName: APK_UPDATE_ZK_VALUES.serviceName
		});
		const promises = [
			requestGetApkVersionInfo({
				versionNumber: subAppUpdateInfo.versionCode,
				gameName: subAppUpdateInfo.gameName,
				arch: EAPKArchs.ARMV7
			}),
			requestGetApkVersionInfo({
				versionNumber: subAppUpdateInfo.versionCode,
				gameName: subAppUpdateInfo.gameName,
				arch: EAPKArchs.ARMV8
			})
		];
		const result = await Promise.all(promises);
		const apkVersionArmV7: any = result[0];
		const apkVersionArmV8: any = result[1];
		let subAppUpdateData: any = {};
		if (initialZkValue.success) {
			subAppUpdateData = initialZkValue.value;
			subAppUpdateData.versionCode = subAppUpdateInfo.versionCode;
			subAppUpdateData.isForceUpdate = subAppUpdateInfo.isForceUpdate;
			subAppUpdateData.apkUrlArmV8 = apkVersionArmV8?.apkLinkUrl;
			subAppUpdateData.apkUrlArmV7 = apkVersionArmV7?.apkLinkUrl;
		}
		return postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_KEY,
			queryParams: {
				serviceName: APK_UPDATE_ZK_VALUES.serviceName,
				game: 'ludo',
				key: subAppUpdateInfo.gameName.toUpperCase(),
				appName
			},
			body: {
				data: subAppUpdateData,
				agent: _get(user, 'userName', ''),
				desc: 'adding sub app updates for ludo'
			}
		});
	}, []);

	const requestUpdateSubAppUpdateInfo = useCallback((subAppUpdateInfo: any) => {
		return requestCreateSubAppUpdateInfo(subAppUpdateInfo);
	}, []);

	const requestToggleAppUpdateInfo = useCallback((websiteUpdateInfoId: string) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/switchOnOffAppUpdate',
			body: { _id: websiteUpdateInfoId },
			queryParams: {
				appName
			}
		});
	}, []);

	const requestUploadApk = useCallback((formData: any) => {
		return postData({
			endpoint: '/marketing/dashboard/apkUpdateInfo/uploadApk',
			body: formData,
			customHeaders: { 'Content-Type': 'multipart/form-data' },
			queryParams: {
				appName
			}
		});
	}, []);

	const deleteSuperAppUpdate = useCallback(async (rowData: any) => {
		const appUpdateInfo = { ...rowData };
		let result = null;
		let { serviceName, keyName } = APK_UPDATE_ZK_VALUES;
		if (appUpdateInfo.isSegmentWiseUpdateReq) {
			serviceName = APK_UPDATE_SEGMENT_ZK_VALUES.serviceName;
			keyName = APK_UPDATE_SEGMENT_ZK_VALUES.keyName;
		}
		result = await getKeyValue({ keyName, serviceName });
		if (result.success && !appUpdateInfo.isSegmentWiseUpdateReq) {
			delete result.value[`${appUpdateInfo.minVersion}-${appUpdateInfo.maxVersion}`];
		} else if (result.success && appUpdateInfo.isSegmentWiseUpdateReq) {
			const sourceAppVersion: string = `${appUpdateInfo.minVersion}-${appUpdateInfo.maxVersion}`;
			const index = getIndexOfSegmentAppUpdate(result.value, sourceAppVersion);
			if (index > -1) {
				result.value.splice(index, 1);
			}
		}
		return postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_KEY,
			queryParams: {
				serviceName,
				game: 'ludo',
				key: keyName,
				appName
			},
			body: {
				data: result.value,
				agent: _get(user, 'userName', ''),
				desc: 'adding app updates for ludo'
			}
		});
	}, []);

	const getDashBoardNamesOptions = useCallback(async () => {
		const dashboardsResponse = await requestGetDashBoardNames();
		const result = _map(_get(dashboardsResponse.data, 'dashBoardsName', []), (dashboardName) => ({
			id: dashboardName,
			label: dashboardName
		}));
		return result;
	}, []);

	const getTestBucketsNamesOptions = useCallback(async () => {
		const dashBoardName: string = 'paidPages';
		if (!dashBoardName) return [];
		const testBucketsResponse = await requestGetTestBucketsNames();
		const result = _map(_get(testBucketsResponse.data, `testBucketsName.${dashBoardName}`, []), (bucketName) => ({
			id: bucketName,
			label: bucketName
		}));
		return result;
	}, []);

	return {
		requestGetApkVersionInfoList,
		requestGetWebsiteUpdateInfoList,
		requestGetAppUpdateInfoList,
		requestGetAppUpdatesList,
		requestCreateApkVersionInfo,
		requestCreateWebsiteUpdateInfo,
		requestCreateAppUpdateInfo,
		requestUpdateApkVersionInfo,
		requestUpdateWebsiteUpdateInfo,
		requestUpdateAppUpdateInfo,
		requestDeployWebsiteUpdateInfo,
		getVersionNumberOptions,
		requestToggleAppUpdateInfo,
		requestUploadApk,
		requestGetSubAppUpdateInfoList,
		requestCreateSubAppUpdateInfo,
		requestUpdateSubAppUpdateInfo,
		deleteSuperAppUpdate,
		requestGetSuperAppUpdateLogs,
		requestGetSubAppUpdateLogsInfo,
		getVersionNumberFromKey,
		filterVersionCodesByGameName,
		getLudoVersionNumberOptions,
		requestDeployWebsiteUpdateInfoV2,
		requestGetWebsiteUpdateInfoListV2,
		requestCreateWebsiteUpdateInfoV2,
		requestGetDashBoardNames,
		getDashBoardNamesOptions,
		requestUpdateWebsiteUpdateInfoV2,
		getTestBucketsNamesOptions
	};
};

export default useService;
